import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks } from "@/components/eeat-blocks";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/buitenland";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { SITE } from "@/lib/site";

const PATH = "/online-casino-buitenland";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/online-casino-buitenland")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Online Casino Buitenland: Legaal, Betrouwbaar? Eerlijke Gids" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Online Casino Buitenland: Legaal, Betrouwbaar? Eerlijke Gids" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("buitenland"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            datePublished: "2026-10-16",
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Online casino buitenland", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: BuitenlandPage,
});

const COMPARE = [
  { t: "Cruks-controle", nl: true, bl: false },
  { t: "Verplichte stortlimiet", nl: true, bl: false },
  { t: "Klachtenroute in Nederland", nl: true, bl: false },
  { t: "Kansspelbelasting afgedragen", nl: true, bl: false },
  { t: "Eigen geld altijd opneembaar", nl: true, bl: false },
];

function CompareInfographic() {
  return (
    <figure id="infographic-buitenland-bescherming" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">Bescherming: vergunde aanbieder vs. online casino buitenland</figcaption>
      <div className="mt-5 overflow-hidden rounded-lg border border-border">
        <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 bg-secondary/60 px-4 py-2 text-xs font-semibold text-muted-foreground">
          <span>Bescherming</span><span>NL-vergunning</span><span>Buitenland</span>
        </div>
        {COMPARE.map((r) => (
          <div key={r.t} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 border-t border-border px-4 py-2.5 text-sm">
            <span className="text-foreground">{r.t}</span>
            <span className="w-24 text-center font-semibold text-primary">Ja</span>
            <span className="w-20 text-center font-semibold text-destructive">Nee</span>
          </div>
        ))}
      </div>
      <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">Bron: Wet Kansspelen op afstand, redactie {SITE.name}, november 2026</p>
    </figure>
  );
}

function LicenceCheckInfographic() {
  const steps = [
    { t: "Onderkant site", d: "Zoek vergunning en bedrijfsnaam" },
    { t: "Bedrijfsnaam", d: "Niet de merknaam, de BV of Ltd" },
    { t: "Register", d: "Opzoeken in openbaar register" },
    { t: "Limieten", d: "Vraagt de site om een stortlimiet?" },
    { t: "Cruks", d: "Wordt je BSN gecontroleerd?" },
  ];
  return (
    <figure id="infographic-buitenland-vergunning" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">Vergunning controleren in vijf stappen</figcaption>
      <ol className="mt-5 grid gap-3 sm:grid-cols-5">
        {steps.map((s, i) => (
          <li key={s.t} className="rounded-lg border border-border bg-secondary/50 p-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
            <p className="mt-2 text-sm font-semibold text-foreground">{s.t}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.d}</p>
          </li>
        ))}
      </ol>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">Ontbreekt één van deze stappen? Dan heeft de site geen Nederlandse vergunning.</p>
    </figure>
  );
}

function BonusMathInfographic() {
  const rows = [
    { l: "Bonus", v: "€ 1.000", w: 4 },
    { l: "Inzetvereiste 50x", v: "€ 50.000 inzetten", w: 100 },
    { l: "Verwacht verlies bij 96% RTP", v: "± € 2.000", w: 8 },
  ];
  return (
    <figure id="infographic-buitenland-bonus" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">Rekenvoorbeeld: wat een grote bonus echt kost</figcaption>
      <ul className="mt-5 space-y-4">
        {rows.map((r, i) => (
          <li key={r.l}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span className="font-medium text-foreground">{r.l}</span>
              <span className="tabular-nums text-muted-foreground">{r.v}</span>
            </div>
            <div className="mt-1.5 h-3 overflow-hidden rounded-full bg-secondary">
              <div className={i === 2 ? "h-full rounded-full bg-destructive" : "h-full rounded-full bg-primary"} style={{ width: `${Math.max(4, r.w)}%` }} />
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">Illustratieve berekening, geen meting. Werkelijke uitkomsten verschillen per sessie.</p>
    </figure>
  );
}

function BuitenlandPage() {
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "Alleen aanbieders met een Nederlandse vergunning zijn legaal voor Nederlandse spelers.",
    "Een buitenlands bedrijf mét Nederlandse vergunning is gewoon legaal.",
    "iDEAL bij buitenlandse goksites loopt vaak via een onbekende tussenpartij.",
    "Winst bij een site zonder NL-vergunning moet je vaak zelf aangeven voor kansspelbelasting.",
    "Uitbetalingen bij buitenlandse sites lopen vaker vast of worden ingehouden.",
    "Controleer de vergunning in één minuut via het openbare register.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Online casino buitenland</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        Online casino buitenland: legaal, betrouwbaar en wat het je kost
      </h1>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <div>
            <KeyTakeaways items={takeaways} />
          </div>

          <div className="mt-10">
            <CompareInfographic />
          </div>

          <div className="mt-10">
            <LicenceCheckInfographic />
          </div>

          <section className="mt-12" aria-label="Alles over online casino buitenland">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14 grid gap-6">
            <BonusMathInfographic />
          </div>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="online casino buitenland"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over online casino buitenland
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
